import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useLang } from "../i18n";
import Seo from "../components/Seo";
import { Eyebrow, Reveal } from "../components/motion";
import { ArrowRight } from "lucide-react";

const IMGS = {
  "private-chef": "https://images.pexels.com/photos/4253315/pexels-photo-4253315.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "a-la-carte": "https://images.pexels.com/photos/36430088/pexels-photo-36430088.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  bespoke: "https://images.unsplash.com/photo-1600891964092-4316c288032e?w=1200&q=80",
  events: "https://images.pexels.com/photos/39405349/pexels-photo-39405349.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  corporate: "https://images.pexels.com/photos/14646752/pexels-photo-14646752.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  luxury: "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
};
const ROUTES = { events: "events", corporate: "corporate", bespoke: "bespoke" };

export default function Experiences() {
  const { t, lang } = useLang();
  const { data: experiences, isLoading } = useQuery({
    queryKey: ["experiences", lang],
    queryFn: () => api.get(`/experiences?lang=${lang}`).then((r) => r.data),
  });

  return (
    <div className="pt-[72px]">
      <Seo title={`${t("exp.title")} — Chef à Domicile`} description={t("exp.sub")} />
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 py-16 md:py-24">
        <Reveal>
          <Eyebrow>{t("nav.experiences")}</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory mb-4">{t("exp.title")}</h1>
          <p className="text-neutral-400 max-w-2xl mb-16">{t("exp.sub")}</p>
        </Reveal>
        {isLoading && <p className="text-neutral-500 py-16 text-center">{t("common.loading")}</p>}
        <div className="space-y-20 md:space-y-28">
          {(experiences || []).map((x, i) => (
            <Reveal key={x.id}>
              <div className={`grid lg:grid-cols-2 gap-10 items-center ${i % 2 ? "lg:[direction:rtl]" : ""}`} data-testid={`experience-detail-${x.slug}`}>
                <div className="overflow-hidden [direction:ltr]">
                  <img src={x.image || IMGS[x.slug]} alt={x.name} loading="lazy" className="w-full aspect-[16/10] object-cover hover:scale-105 transition-transform duration-[1.4s]" />
                </div>
                <div className="[direction:inherit] lg:px-8" dir="inherit">
                  <span className="font-serif text-5xl text-gold/20">0{i + 1}</span>
                  <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-ivory mt-3 mb-4">{x.name}</h2>
                  <p className="text-neutral-400 leading-relaxed mb-8">{x.description}</p>
                  <Link to={`/${lang}/${ROUTES[x.slug] || "chefs"}`} data-testid={`experience-cta-${x.slug}`}
                    className="inline-flex items-center gap-2 text-sm text-gold gold-underline">
                    {t("nav.findChef")} <ArrowRight size={14} className="rtl:rotate-180" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
